import React from 'react';
import Card from 'react-bootstrap/Card';
import Button from "react-bootstrap/Button";
import { OrderItem } from 'store/slices/tOrderTypes';
import ARIA_LABELS from 'constants/ariaLabels';
import QuantityButton from 'app/features/Order/CartItem/ButtonQuantity';
import { formatPrice, getUniqueBulkModifierOptions, getModifierLabel } from 'utilities/menuItemUtils';
import ImageWithFallback from 'components/ImageWithFallback';

type Props = {
    cartItem: OrderItem;
    addToCart: (selectedItem: OrderItem) => void;
    removeFromCart: (item: OrderItem) => void;
    removeGroupFromCart: (item: OrderItem) => void;
    editItemFromCart: (item: OrderItem) => void;
};

const CartItem: React.FC<Props> = ({
                                       cartItem,
                                       addToCart,
                                       removeFromCart,
                                       removeGroupFromCart,
                                       editItemFromCart,
                                   }) => {
        /**
         * Transforming the modifiers list to have show only one entry for each
         * bulk modifier group as per business requirement
         */

        const deDuplicatedModifierOptions = getUniqueBulkModifierOptions(cartItem);

        return (
        <Card className="shadow-sm m-2 mb-4" style={{borderRadius: '20px'}}>
            <Card.Header className="d-flex flex-column cart-header">
                <span aria-label={ARIA_LABELS.CART.productName} className="font-main fw-bold">{cartItem.itemName}</span>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-between p-2">
                    <div className="horizontalAlignCenter justify-content-between">
                        <ImageWithFallback
                            src={cartItem.imageThumb}
                            alt={cartItem.itemName}
                            className="card-img menuItemThumb"
                        />
                        <div className="itemQuantitySection">
                            <QuantityButton
                                itemValue="-"
                                onClick={() => removeFromCart(cartItem)}
                                ariaLabel={ARIA_LABELS.CART.decreaseQuantity}
                            />
                            <div aria-live="polite">
                            <Card.Text className="font-sub itemQuantity" aria-label={ARIA_LABELS.CART.currentQuantity(cartItem?.quantity || 0)}>{cartItem.quantity}</Card.Text>
                            </div>
                            <QuantityButton
                                onClick={() => addToCart(cartItem)}
                                itemValue="+"
                                ariaLabel={ARIA_LABELS.CART.increaseQuantity}
                            />
                        </div>
                    </div>
                    <div>
                        {/* Default recipe modifiers shouldn't be displayed.
                        Hence filtering them out*/}
                        {deDuplicatedModifierOptions
                            .filter((modifier) => modifier.isUserSelectedOption === true)
                            .map((modifier) => (
                            <div className="d-flex flex-row justify-content-between font-sub" key={cartItem.isBulk ? modifier.id : modifier.modifierGroupId}>
                                <span aria-label={ARIA_LABELS.CART.status}>
                                    {
                                        getModifierLabel(
                                            modifier.modifierGroupName || "",
                                            modifier.optionName,
                                        )
                                    }
                                </span>
                                {
                                    (Number(modifier?.price) > 0 && modifier?.modifierGroupName !== "Size") && (
                                        <span
                                            className="font-sub"
                                            aria-label={ARIA_LABELS.CART.subItemPrice}
                                            role="note"
                                        >
                                            {`(+$${formatPrice(modifier?.price || 0)})`}
                                        </span>
                                    )
                                }
                            </div>
                        ))}
                    </div>
            </Card.Body>
            <Card.Footer className="d-flex flex-row justify-content-between align-items-center p-1 cart-footer">
                <div>
                    <Button variant="link" className="font-sub" aria-label={ARIA_LABELS.CART.removedItem} onClick={() => removeGroupFromCart(cartItem)}>
                        Remove
                    </Button>
                    <Button variant="link" className="font-sub" aria-label={ARIA_LABELS.CART.editItem} onClick={() => editItemFromCart(cartItem)}>
                        Edit
                    </Button>
                </div>
                <b className="m-1 font-main">
                    Total:&nbsp;
                    <span aria-label={ARIA_LABELS.CART.totalItemPrice}>
                        $
                        {formatPrice(cartItem.total || 0)}
                    </span>
                </b>
            </Card.Footer>
        </Card>
    );
};

export default CartItem;
